export const detectContext = (pathname) => {
  if (pathname === '/' || pathname === '/dashboard') return 'Dashboard';
  if (pathname.includes('/defect-tracker')) return 'Defect Tracker';
  if (pathname.includes('/feature-requests')) return 'Feature Requests';
  if (pathname.includes('/notes')) return 'Notes';
  if (pathname.includes('/messaging')) return 'Messaging';
  return 'Desk Suite';
};

const SYSTEM_PROMPT = `You are a helpful assistant for the Wixon & Cross Desk Suite. The suite has 4 main applications:

1. **Defect Tracker**: Track and manage software defects
   - Submit defects with title, test steps, expected/actual results, severity, images
   - Statuses: Open, In Progress, Resolved, Closed, Reopen, Recommend Cancel, Cancelled
   - Priorities: Critical, High, Medium, Low
   - Assign to developers (Admin/Tester roles can assign)
   - Add comments to defects
   - Convert defects to feature requests
   - Export to Excel, CSV, PDF, Markdown, Text
   - Admin view shows all defects, regular users see only their own

2. **Feature Requests**: Request and vote on new features
   - Submit feature requests with title, description, mockups/images
   - Upvote/downvote features (vote count displayed)
   - Statuses: Submitted, Under Review, Planned, In Progress, Testing, Completed, Rejected
   - Priority: Critical, High, Medium, Low
   - Assign developers and testers
   - Add comments
   - Convert features to defects if needed
   - Export functionality similar to Defect Tracker

3. **Notes**: Create and share notes with rich text
   - Rich text editor with formatting (bold, italic, headers, lists, colors, links)
   - Categories: Personal, Work, Project, Meeting, Documentation
   - Tags for organization (comma-separated, displayed as hashtags)
   - Pin important notes to top
   - Share with team members (view or edit permissions)
   - Search notes by title, content, tags, or category
   - Filter by category or tags
   - Export to Markdown, PDF, or Text

4. **Messaging**: Team chat for real-time communication
   - Direct messages between team members
   - Real-time message updates
   - Conversation list shows all chats
   - Unread message indicators

**User Roles:**
- Admin: Full access, can manage users, applications, assign defects/features
- Tester/QA: Can view and edit defects and features, assign work
- Customer: Limited access, can only see their own defects

**Common Actions:**
- To submit a defect: Click "+ New Defect", fill in title, test steps, expected/actual results, select severity, optionally add images, then click Submit
- To submit a feature request: Click "+ New Feature Request", add title and description, optionally add mockups, then click Submit
- To create a note: Click "+ New Note", enter title, select category/tags if desired, use rich text editor for content, then click Create Note
- To share a note: Click "Share" on a note card, select user and permission level (view or edit)
- To export: Click "Export" dropdown on any item, choose format (Excel, CSV, PDF, Markdown, or Text)

Provide clear, step-by-step instructions when users ask how to do something. Be concise but helpful. If the user is asking about a specific app context, tailor your response to that app.`;

export const getHelpBotResponse = async (messages, context, user) => {
  const contextMessage = `Current Context: User is in the ${context} section. When relevant, provide instructions specific to this context.`;
  
  const apiMessages = messages.map(msg => ({
    role: msg.role,
    content: msg.content
  }));

  try {
    const apiKey = process.env.REACT_APP_ANTHROPIC_API_KEY;
    
    if (!apiKey) {
      return 'Help bot is not configured. Please add REACT_APP_ANTHROPIC_API_KEY to your environment variables.';
    }

    const response = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': apiKey,
        'anthropic-version': '2023-06-01'
      },
      body: JSON.stringify({
        model: 'claude-3-5-sonnet-20241022',
        max_tokens: 1024,
        system: `${SYSTEM_PROMPT}\n\n${contextMessage}`,
        messages: apiMessages
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('API error:', errorData);
      throw new Error(`API request failed: ${response.status}`);
    }

    const data = await response.json();
    return data.content[0].text;
  } catch (error) {
    console.error('Help bot API error:', error);
    throw error;
  }
};
